import { Box, Button, CheckBox, FormField, Spinner, Text, TextInput, Tip } from 'grommet';
import { Checkmark, CircleInformation, Location } from 'grommet-icons';
import { Controller, useFormContext } from 'react-hook-form';

import { useGetStateLookups } from '@/api/use-states';
import { useZipCodeLookup } from '@/api/use-zip-code-lookup';
import { User } from '@/features/admin/types/user';
import { Client } from '@/features/clients';
import {
  CreateUpdateClientUserFormSchema,
  CreateUpdateClientUserWithGeoFormSchema
} from '@/features/clients/models/create-update-client-user-form-schema';
import CreateUpdateClientUserFormWithGeo from '@/features/clients/components/create-update-client-user-form-with-geo';

type Props = {
  client: Client;
  user?: User;
}

const CreateUpdateClientUserWithGeoForm = ({ client, user }: Props) => {
  const { data: stateLookups = [] } = useGetStateLookups();
  const form = useFormContext<CreateUpdateClientUserWithGeoFormSchema>();
  const watchedZipCode = form.watch('zipCode');
  const watchedCity = form.watch('city');
  const watchedStateId = form.watch('stateId');

  const { data: zipLookup, isLoading: isLookingUp, error: lookupError } = useZipCodeLookup(watchedZipCode);

  const handleSelectSuggestion = () => {
    if (!zipLookup) return;

    const stateId = stateLookups.find(s => s.code === zipLookup.stateCode)?.id;
    if (stateId) {
      form.setValue('city', zipLookup.city);
      form.setValue('stateId', stateId);
    }
  };

  const getStateName = (stateId: number | null | undefined) => {
    if (!stateId) return '';
    return stateLookups.find(s => s.id === stateId)?.label ?? '';
  };

  // @ts-expect-error TS(2532): Object is possibly 'undefined'.
  const featureOptionsCapsule = client.subscription.premiumFeatures.filter(feature => !feature.key.endsWith('_mobile') && feature.key !== 'ai_insights').map(feature => ({
    key: feature.key,
    name: feature.name,
    disabled: !feature.isEnabled
  }));

    // @ts-expect-error TS(2532): Object is possibly 'undefined'.
  const featureOptionsMobile = client.subscription.premiumFeatures.filter(feature => feature.key.endsWith('_mobile')).map(feature => ({
    key: feature.key,
    name: feature.name,
    disabled: !feature.isEnabled
  }));

  const getEnabledFeatures = (user: User, isMobile = false) => {
    if(isMobile){
          return user?.premiumFeatures
      ?.filter(feature => feature.isEnabled && feature.key.endsWith('_mobile'))
      .map(feature => feature.key);
    }else{
      return user?.premiumFeatures
        ?.filter(feature => feature.isEnabled && !feature.key.endsWith('_mobile'))
        .map(feature => feature.key);
    }
  };

  // Check if the suggestion matches currently selected values
  const suggestionAlreadyApplied = zipLookup &&
    watchedCity === zipLookup.city &&
    stateLookups.find(s => s.id === watchedStateId)?.code === zipLookup.stateCode;

  return (
    <form>
      <Controller
        control={form.control}
        name="name"
        defaultValue={user?.name ?? ''}
        render={({ field, fieldState: { error } }) => (
          <FormField
            required
            name={field.name}
            label="Name"
            error={error?.message}
          >
            <TextInput {...field}/>
          </FormField>
        )}
      />
      <Controller
        control={form.control}
        name="email"
        defaultValue={user?.email ?? ''}
        render={({ field, fieldState: { error } }) => (
          <FormField
            required
            name={field.name}
            label="Email"
            error={error?.message}
          >
            <TextInput {...field}/>
          </FormField>
        )}
      />

      <Controller
        control={form.control}
        name="zipCode"
        defaultValue={user?.zipCode ?? ''}
        render={({ field, fieldState: { error } }) => (
          <FormField
            name={field.name}
            label="Zip Code"
            help="Enter a zip code to automatically populate city and state"
            error={error?.message}
          >
            <TextInput
              {...field}
              placeholder="Enter 5-digit zip code"
              maxLength={5}
            />
          </FormField>
        )}
      />
      {isLookingUp && (
        <Box direction="row" align="center" gap="small" margin={{ bottom: 'small' }}>
          <Spinner size="xsmall" />
          <Text size="small">Looking up zip code...</Text>
        </Box>
      )}
      {lookupError && (
        <Box margin={{ bottom: 'small' }}>
          <Text size="small" color="status-error">
            {lookupError.message || 'Invalid zip code'}
          </Text>
        </Box>
      )}
      {zipLookup && !suggestionAlreadyApplied && (
        <Box margin={{ bottom: 'small' }}>
          <Button
            onClick={handleSelectSuggestion}
            hoverIndicator
            style={{ borderRadius: '20px', width: 'fit-content' }}
          >
            <Box
              direction="row"
              align="center"
              gap="small"
              pad={{ vertical: 'xsmall', horizontal: 'small' }}
              background="light-3"
              round="large"
            >
              <Location size="small" />
              <Text size="small" weight={500}>
                {zipLookup.city}, {zipLookup.stateCode}
              </Text>
              <Checkmark size="small" color="status-ok" />
            </Box>
          </Button>
        </Box>
      )}
      <Controller
        control={form.control}
        name="city"
        defaultValue={user?.city ?? ''}
        render={({ field, fieldState: { error } }) => (
          <FormField
            name={field.name}
            label="City"
            error={error?.message}
          >
            <TextInput
              {...field}
              value={field.value ?? ''}
              disabled
              placeholder="City will be populated from zip code"
            />
          </FormField>
        )}
      />
      <Controller
        control={form.control}
        name="stateId"
        defaultValue={user?.stateId}
        render={({ field, fieldState: { error } }) => (
          <FormField
            name={field.name}
            label="State"
            error={error?.message}
          >
            <TextInput
              value={getStateName(field.value)}
              disabled
              placeholder="State will be populated from zip code"
            />
          </FormField>
        )}
      />
      <Controller
        control={form.control}
        name="enabledFeaturesCapsule"
        // @ts-expect-error TS(2345): Argument of type 'User | undefined' is not assigna... Remove this comment to see the full error message
        defaultValue={getEnabledFeatures(user, false) ?? []}
        render={({ field }) => (
          <Box>
            <Text margin={{ vertical: 'small' }} weight="bold">Features (Capsule)</Text>
            <Box gap="small">
              {
                featureOptionsCapsule.map(option => (
                  <Box direction="row" gap="small" align="center" key={option.key}>
                    <CheckBox
                      label={option.name}
                      disabled={option.disabled}
                      value={option.key}
                      checked={field.value.includes(option.key)}
                      onChange={({ target }) => target.checked
                        ? field.onChange([...field.value, target.value])
                        : field.onChange(field.value.filter((val: string) => val !== option.key))
                      }
                    />
                    {option.disabled && (
                      <Tip
                        content={`"${option.name}" is not enabled for this client.`}
                      >
                        <CircleInformation size="small"/>
                      </Tip>
                    )
                    }
                  </Box>
                ))
              }
            </Box>
          </Box>
        )}
      />

      <Controller
        control={form.control}
        name="enabledFeaturesMobile"
        // @ts-expect-error TS(2345): Argument of type 'User | undefined' is not assigna... Remove this comment to see the full error message
        defaultValue={getEnabledFeatures(user, true) ?? []}
        render={({ field }) => (
          <Box>
            <Text margin={{ vertical: 'small' }} weight="bold">Features (Mobile)</Text>
            <Box gap="small">
              {
                featureOptionsMobile.map(option => (
                  <Box direction="row" gap="small" align="center" key={option.key}>
                    <CheckBox
                      label={option.name}
                      disabled={option.disabled}
                      value={option.key}
                      checked={field.value.includes(option.key)}
                      onChange={({ target }) => target.checked
                        ? field.onChange([...field.value, target.value])
                        : field.onChange(field.value.filter((val: string) => val !== option.key))
                      }
                    />
                    {option.disabled && (
                      <Tip
                        content={`"${option.name}" is not enabled for this client.`}
                      >
                        <CircleInformation size="small"/>
                      </Tip>
                    )
                    }
                  </Box>
                ))
              }
            </Box>
          </Box>
        )}
      />
    </form>
  );
};

export default CreateUpdateClientUserWithGeoForm;
